import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { Button } from "../components/ui/button";
import { trackCTAClick } from "../utils/analytics";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";

const ROWS = [
  {
    capability: "Clause-level citations from your own agreement",
    cbaiq: "Every answer cites the specific article and language from your uploaded agreement.",
    general: "Answers from general training data; cannot reliably cite your agreement's actual clauses.",
  },
  {
    capability: "Multi-document context (agreement + LOUs + past settlements)",
    cbaiq: "Reads your agreement together with its letters of understanding and settlement history.",
    general: "Limited context windows and no persistent, indexed document set for your bargaining unit.",
  },
  {
    capability: "Risk-scored case review",
    cbaiq: "Structured scenario analysis with grievance-risk scoring and procedural requirements.",
    general: "Free-form text only; no structured risk assessment.",
  },
  {
    capability: "Exportable audit trail",
    cbaiq: "Answer history and exportable documentation you can bring to a grievance meeting.",
    general: "Chat transcripts that are hard to export, reference, or stand behind later.",
  },
  {
    capability: "Data not used for training",
    cbaiq: "Agreements are never used for model training and never externally indexed.",
    general: "Depends on plan and settings; consumer tiers may use conversations to improve models.",
  },
  {
    capability: "Purpose-built labour relations terminology",
    cbaiq: "Built around CBA structures: seniority, bumping, past practice, LOUs, grievance steps.",
    general: "General-purpose vocabulary; can conflate labour relations terms across jurisdictions.",
  },
];

const VS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "CBAIQ vs ChatGPT & Copilot for Collective Agreements",
  "description": "An honest comparison of CBAIQ against general AI tools (ChatGPT, Copilot) for collective agreement interpretation: citations, multi-document context, risk scoring, audit trails, and data handling.",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://cbaiq.com/cbaiq-vs-chatgpt" },
  "author": { "@type": "Organization", "name": "CBAIQ" },
  "publisher": { "@type": "Organization", "name": "CBAIQ", "url": "https://cbaiq.com" },
};

export default function CbaiqVsChatgpt() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="vs-chatgpt-page">
      <Seo
        title="CBAIQ vs ChatGPT & Copilot for Collective Agreements"
        description="Can you use ChatGPT to interpret a collective agreement? An honest comparison: clause-level citations, LOU and settlement context, risk-scored case review, audit trails, and data handling — CBAIQ vs general AI."
        path="/cbaiq-vs-chatgpt"
        jsonLd={VS_JSONLD}
      />
      <PublicHeader />

      <div className="flex-1">
        <section className="pt-20 sm:pt-24 pb-12 px-4" data-testid="vs-hero">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">Comparison</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground mb-5">
              CBAIQ vs ChatGPT &amp; Copilot for Collective Agreements
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              Your supervisors are probably already pasting agreement language into ChatGPT. Here's an honest
              look at where general AI tools work well, and where a purpose-built tool earns its keep.
            </p>
          </div>
        </section>

        {/* What general AI does well */}
        <section className="pb-12 px-4" data-testid="vs-honest-section">
          <div className="max-w-3xl mx-auto border border-border rounded-lg p-7 bg-muted/20">
            <h2 className="text-base font-semibold text-foreground mb-3">Where ChatGPT and Copilot are genuinely useful</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              General AI tools are excellent at drafting communications, summarizing publicly available
              information, explaining general labour relations concepts, and cleaning up writing. If that's
              the whole job, you don't need CBAIQ. The gap appears when the question is about{" "}
              <span className="text-foreground font-medium">your specific agreement</span> — and the answer
              has to hold up in a grievance meeting.
            </p>
          </div>
        </section>

        {/* Comparison table */}
        <section className="pb-16 px-4" data-testid="vs-table-section">
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="vs-comparison-table">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-4 pr-4 font-semibold text-foreground w-1/4">Capability</th>
                  <th className="text-left py-4 px-4 font-semibold text-foreground w-[37.5%]">CBAIQ</th>
                  <th className="text-left py-4 pl-4 font-semibold text-muted-foreground w-[37.5%]">ChatGPT / Copilot</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map(({ capability, cbaiq, general }) => (
                  <tr key={capability} className="border-b border-border/60 align-top">
                    <td className="py-5 pr-4 font-medium text-foreground">{capability}</td>
                    <td className="py-5 px-4 text-muted-foreground">
                      <span className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-foreground/70 mt-0.5 flex-shrink-0" />
                        <span>{cbaiq}</span>
                      </span>
                    </td>
                    <td className="py-5 pl-4 text-muted-foreground">
                      <span className="flex items-start gap-2">
                        <Minus className="w-4 h-4 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                        <span>{general}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Bottom line */}
        <section className="pb-16 px-4" data-testid="vs-bottom-line">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">The bottom line</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Use general AI for drafting and general research. Use CBAIQ when the question is "what does{" "}
              <em>our</em> agreement allow?" — because that answer needs a citation, the surrounding LOUs and
              settlements, a risk score, and a record you can export. Learn more about{" "}
              <Link to="/security" className="text-foreground font-medium underline underline-offset-4">how CBAIQ handles your documents</Link>{" "}
              or <Link to="/how-it-works" className="text-foreground font-medium underline underline-offset-4">how it works</Link>.
            </p>
          </div>
        </section>

        {/* Pilot CTA */}
        <section className="py-16 border-t border-border/40 bg-muted/20 px-4" data-testid="vs-cta-section">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-4">
              Test it on your own agreement.
            </h2>
            <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
              Guided 14-day pilot — upload one agreement, get 20 answered questions, onboarding call included. No credit card.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto" data-testid="vs-start-pilot-btn" onClick={() => trackCTAClick('vs_chatgpt_start_pilot')}>
                  Start a guided pilot
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="vs-view-pricing-btn" onClick={() => trackCTAClick('vs_chatgpt_view_pricing')}>
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
